import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import type { PlatformServices, StorageService, AppService, WOLService, UpdateService, UpdateInfo } from './types';
import { Wol } from './wolPlugin';
import { fetchVersionIndex, fetchVersionManifest } from '../operatorUpdate';

const capacitorStorage: StorageService = {
  get: async (key) => {
    const { value } = await Preferences.get({ key });
    return value;
  },
  set: async (key, value) => {
    await Preferences.set({ key, value });
  },
  remove: async (key) => {
    await Preferences.remove({ key });
  },
};

const capacitorApp: AppService = {
  quit: async () => {
    await App.exitApp();
  },
};

const capacitorWOL: WOLService = {
  send: (macAddress, ipAddress) => Wol.send({ macAddress, ipAddress }),
};

// The version the app is actually running right now: the id of the active
// Live Update bundle if one has been applied, else the built-in build baked in
// at compile time (__APP_VERSION__ from vite define). Bundle ids ARE version
// strings (see downloadOperatorBundle / publish-android-update.js).
export async function getActiveOperatorVersion(): Promise<string> {
  try {
    const { bundleId } = await LiveUpdate.getCurrentBundle();
    if (bundleId) return bundleId.replace(/^v/, '');
  } catch {
    // getCurrentBundle unavailable (shouldn't happen on Android) — fall through.
  }
  return __APP_VERSION__;
}

// Download + verify + stage a specific version's web bundle, then apply it.
// `reload()` swaps to the new bundle and restarts the webview, so this does not
// return on success. Used both by the manual UpdateChecker UI and the silent
// per-device reconcile (operatorDevice.ts).
export async function applyOperatorVersion(version: string): Promise<{ success: boolean; error?: string }> {
  try {
    const manifest = await fetchVersionManifest(version);
    if (!manifest?.bundleUrl) {
      return { success: false, error: `No published bundle for version ${version}` };
    }
    await LiveUpdate.downloadBundle({
      url: manifest.bundleUrl,
      bundleId: manifest.version,
      ...(manifest.checksum ? { checksum: manifest.checksum } : {}),
    });
    await LiveUpdate.setNextBundle({ bundleId: manifest.version });
    await LiveUpdate.reload(); // does not return — webview restarts on the new bundle
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err?.message || 'OTA update failed' };
  }
}

// UpdateService for the manual "version badge → check → update" affordance.
// The primary update path is the silent per-device reconcile in
// operatorDevice.ts; this lets an admin see the version and force the newest
// published build on demand.
const capacitorUpdate: UpdateService = {
  getVersion: () => getActiveOperatorVersion(),

  check: async (): Promise<UpdateInfo> => {
    const currentVersion = await getActiveOperatorVersion();
    const index = await fetchVersionIndex();
    const latest = index?.latest ?? null;
    if (!latest) {
      return { currentVersion, latestVersion: null, updateAvailable: false };
    }
    const manifest = await fetchVersionManifest(latest);
    const latestClean = latest.replace(/^v/, '');
    // Reuse the same compare as the reconcile path via a cheap inline check.
    const updateAvailable =
      latestClean !== currentVersion &&
      // only surface as "available" if the published bundle actually exists
      !!manifest?.bundleUrl;
    return {
      currentVersion,
      latestVersion: latestClean,
      updateAvailable,
      downloadUrl: manifest?.bundleUrl,
      assetName: latestClean,
      assetSize: manifest?.size,
      releaseNotes: manifest?.notes,
      publishedAt: manifest?.publishedAt,
    };
  },

  // assetName carries the target version (see check()).
  install: (_downloadUrl, assetName) => applyOperatorVersion(assetName),
};

export function createCapacitorServices(): PlatformServices {
  return {
    storage: capacitorStorage,
    update: capacitorUpdate, // OTA web-bundle self-update (Capawesome Live Update)
    kiosk: null,             // No kiosk mode on Android
    app: capacitorApp,
    wol: capacitorWOL,
  };
}
